/**
 * Two-option confirm dialog for box restart.
 *
 * Restart controller (~60s SSH/HTTP outage, app process re-spawned)
 * vs. full Reboot (~90s downtime, kernel cycles). Either way the WS
 * client reconnects on its own backoff schedule.
 */

import React from 'react';
import { Power, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FleetDevice } from '../types';

interface RestartConfirmDialogProps {
  device: FleetDevice;
  open: boolean;
  onClose: () => void;
  onConfirm: (kind: 'system.restart_controller' | 'system.reboot') => Promise<void>;
}

export const RestartConfirmDialog: React.FC<RestartConfirmDialogProps> = ({
  device,
  open,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-status-warning">
            <RotateCcw className="size-5" />
            Restart {device.name || device.device_id}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-fg-secondary">
          Both options drop SSH + HTTP for the duration. The cloud dashboard will mark the device
          offline during the window and recover automatically when the WS client reconnects.
        </p>

        <div className="space-y-3">
          <div className="rounded-md border border-border bg-bg-surface p-4">
            <div className="mb-1 flex items-center gap-2">
              <RotateCcw className="size-4 text-status-warning" />
              <span className="font-mono font-semibold text-status-warning">Restart controller</span>
            </div>
            <p className="text-sm text-fg-secondary">
              `systemctl restart radxa-controller` — ~60s downtime. App process re-spawns. Use this
              for picking up new config or recovering a wedged worker thread.
            </p>
          </div>

          <div className="rounded-md border border-border bg-bg-surface p-4">
            <div className="mb-1 flex items-center gap-2">
              <Power className="size-4 text-status-error" />
              <span className="font-mono font-semibold text-status-error">Full reboot</span>
            </div>
            <p className="text-sm text-fg-secondary">
              `sudo reboot` — ~90s downtime. Kernel cycles, USB re-enumerates. Use only for
              hardware-level wedge (modem stuck, USB resource leak, kernel oops).
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-status-warning text-status-warning hover:bg-status-warning/10"
            onClick={() => onConfirm('system.restart_controller')}
          >
            <RotateCcw />
            Restart controller
          </Button>
          <Button
            variant="outline"
            className="border-status-error text-status-error hover:bg-status-error/10"
            onClick={() => onConfirm('system.reboot')}
          >
            <Power />
            Full reboot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RestartConfirmDialog;
